var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { normalizeTooltipLabelWhitespace, parseMmmDInFilterRange } from '@web/utility/ tooltip/tooltipDateLabel';
import { ComparisonPeriod } from '@web/utility/comparisonPeriod';
import { getPreviousPeriod } from '@workalizer/shared/utils';
import { format, getYear, isMatch, isValid, parse, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import React from 'react';
export { ComparisonPeriod };
var PERIOD_PREFIXES = [
    'previousPeriod',
    'currentPeriod',
    'teamAverage',
    'previous',
    'current',
    'team'
];
export function getPeriodComparisonSection(dataKey) {
    if (dataKey === 'previousPeriod' || dataKey.startsWith('previous')) {
        return ComparisonPeriod.Previous;
    }
    if (dataKey === 'teamAverage' || dataKey.startsWith('teamAverage')) {
        return ComparisonPeriod.Team;
    }
    if (dataKey.startsWith('team')) {
        return ComparisonPeriod.Team;
    }
    if (dataKey.startsWith('current')) {
        return ComparisonPeriod.Current;
    }
    if (dataKey === 'docsSharedExternally' ||
        dataKey === 'createdAndOwnedFiles') {
        return ComparisonPeriod.Current;
    }
    return null;
}
export function isPeriodComparisonTooltipDataKeys(dataKeys) {
    if (!dataKeys.length)
        return false;
    return dataKeys.every(function (key) { return getPeriodComparisonSection(key) !== null; });
}
export function getMetricKeyFromPeriodDataKey(dataKey) {
    if (dataKey === 'previousPeriod' ||
        dataKey === 'teamAverage' ||
        dataKey === 'docsSharedExternally' ||
        dataKey === 'createdAndOwnedFiles') {
        return dataKey;
    }
    for (var _i = 0, PERIOD_PREFIXES_1 = PERIOD_PREFIXES; _i < PERIOD_PREFIXES_1.length; _i++) {
        var prefix = PERIOD_PREFIXES_1[_i];
        if (dataKey.startsWith(prefix)) {
            var rest = dataKey.slice(prefix.length);
            if (!rest)
                return dataKey;
            return rest.charAt(0).toLowerCase() + rest.slice(1);
        }
    }
    return dataKey;
}
function isMonthYearPeriodLabel(trimmed) {
    if (!/^[A-Za-z]{3,9}\s+\d{4}$/.test(trimmed))
        return false;
    return isMatch(trimmed, 'MMM yyyy', { locale: enUS });
}
export function getTooltipPeriodReferenceYear(dateFrom, dateTo) {
    var to = parseISO(dateTo.slice(0, 10));
    if (isValid(to)) {
        return getYear(to);
    }
    var from = parseISO(dateFrom.slice(0, 10));
    if (isValid(from)) {
        return getYear(from);
    }
    return getYear(new Date());
}
function formatDateForPeriodComparison(date, referenceYear) {
    if (getYear(date) === referenceYear) {
        return format(date, 'MMM d', { locale: enUS });
    }
    return format(date, 'MMM d, yyyy', { locale: enUS });
}
export function formatPeriodComparisonDateRange(from, to, referenceYear) {
    var fromLabel = formatDateForPeriodComparison(from, referenceYear);
    var toLabel = formatDateForPeriodComparison(to, referenceYear);
    if (fromLabel === toLabel) {
        return fromLabel;
    }
    return "".concat(fromLabel, " - ").concat(toLabel);
}
function parseMmmDWithOptionalYear(part, dateFrom, dateTo) {
    var trimmed = part.trim();
    if (isMatch(trimmed, 'MMM d, yyyy', { locale: enUS })) {
        var d = parse(trimmed, 'MMM d, yyyy', new Date(), { locale: enUS });
        return isValid(d) ? d : null;
    }
    return parseMmmDInFilterRange(trimmed, dateFrom, dateTo);
}
function parsePeriodComparisonRangeLabel(label, dateFrom, dateTo) {
    var parts = label.split(' - ').map(function (part) { return part.trim(); });
    if (parts.length !== 2)
        return null;
    var from = parseMmmDWithOptionalYear(parts[0], dateFrom, dateTo);
    var to = parseMmmDWithOptionalYear(parts[1], dateFrom, dateTo);
    if (!from || !to)
        return null;
    return { from: from, to: to };
}
export function normalizePeriodComparisonLabel(label, dateFrom, dateTo, referenceYear) {
    if (referenceYear === void 0) { referenceYear = getTooltipPeriodReferenceYear(dateFrom, dateTo); }
    var trimmed = normalizeTooltipLabelWhitespace(label).trim();
    if (!trimmed)
        return trimmed;
    if (isMonthYearPeriodLabel(trimmed)) {
        var monthDate = parse(trimmed, 'MMM yyyy', new Date(), { locale: enUS });
        if (isValid(monthDate) && getYear(monthDate) === referenceYear) {
            return format(monthDate, 'MMM', { locale: enUS });
        }
        return trimmed;
    }
    var range = parsePeriodComparisonRangeLabel(trimmed, dateFrom, dateTo);
    if (range) {
        return formatPeriodComparisonDateRange(range.from, range.to, referenceYear);
    }
    var singleDay = parseMmmDInFilterRange(trimmed, dateFrom, dateTo);
    if (singleDay) {
        return formatDateForPeriodComparison(singleDay, referenceYear);
    }
    return trimmed;
}
export function formatTooltipFilterDateRange(dateFrom, dateTo, referenceYear) {
    if (referenceYear === void 0) { referenceYear = getTooltipPeriodReferenceYear(dateFrom, dateTo); }
    var from = parseISO(dateFrom.slice(0, 10));
    var to = parseISO(dateTo.slice(0, 10));
    if (!isValid(from) || !isValid(to)) {
        return "".concat(dateFrom, " - ").concat(dateTo);
    }
    return formatPeriodComparisonDateRange(from, to, referenceYear);
}
export function resolveCurrentPeriodComparisonLabel(label, dateFrom, dateTo) {
    var referenceYear = getTooltipPeriodReferenceYear(dateFrom, dateTo);
    var trimmed = normalizeTooltipLabelWhitespace(label).trim();
    if (!trimmed) {
        return formatTooltipFilterDateRange(dateFrom, dateTo, referenceYear);
    }
    return normalizePeriodComparisonLabel(trimmed, dateFrom, dateTo, referenceYear);
}
export function resolvePreviousPeriodComparisonLabel(payload, dateFrom, dateTo) {
    var referenceYear = getTooltipPeriodReferenceYear(dateFrom, dateTo);
    var fromPayload = payload === null || payload === void 0 ? void 0 : payload.previousPeriodLabel;
    if (typeof fromPayload === 'string' && fromPayload.trim()) {
        var _a = getPreviousPeriod(dateFrom, dateTo), prevPeriodDateFrom = _a.prevPeriodDateFrom, prevPeriodDateTo = _a.prevPeriodDateTo;
        return normalizePeriodComparisonLabel(fromPayload, prevPeriodDateFrom, prevPeriodDateTo, referenceYear);
    }
    return '';
}
export function resolvePeriodComparisonSectionLabels(label, payload, dateFrom, dateTo) {
    var currentFromPayload = payload === null || payload === void 0 ? void 0 : payload.currentPeriodComparisonLabel;
    var current = typeof currentFromPayload === 'string' && currentFromPayload.trim()
        ? currentFromPayload
        : resolveCurrentPeriodComparisonLabel(label, dateFrom, dateTo);
    return {
        current: current,
        previous: resolvePreviousPeriodComparisonLabel(payload, dateFrom, dateTo)
    };
}
export function groupTooltipEntriesByPeriodSection(rows) {
    var _a;
    var sections = [];
    for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
        var row = rows_1[_i];
        var section = getPeriodComparisonSection(String((_a = row.dataKey) !== null && _a !== void 0 ? _a : ''));
        if (section && !sections.includes(section)) {
            sections.push(section);
        }
    }
    return sections;
}
export function rowsForPeriodComparisonSection(rows, section) {
    return rows.filter(function (row) { var _a; return getPeriodComparisonSection(String((_a = row.dataKey) !== null && _a !== void 0 ? _a : '')) === section; });
}
export function createPeriodComparisonSeriesLabelFormatter(labelsByMetricKey) {
    return function (dataKey) {
        var _a, _b, _c;
        var metricKey = getMetricKeyFromPeriodDataKey(dataKey);
        return ((_c = (_b = (_a = labelsByMetricKey[dataKey]) !== null && _a !== void 0 ? _a : labelsByMetricKey[metricKey]) !== null && _b !== void 0 ? _b : labelsByMetricKey.periodBar) !== null && _c !== void 0 ? _c : metricKey.replace(/([A-Z])/g, ' $1').trim());
    };
}
var SECTION_TITLE_KEYS = (_a = {},
    _a[ComparisonPeriod.Previous] = 'tooltip.periodComparison.previousPeriod',
    _a[ComparisonPeriod.Current] = 'tooltip.periodComparison.currentPeriod',
    _a[ComparisonPeriod.Team] = 'tooltip.periodComparison.teamAverage',
    _a);
export function PeriodComparisonSectionHeader(_a) {
    var section = _a.section, periodLabel = _a.periodLabel, t = _a.t;
    var title = t(SECTION_TITLE_KEYS[section]);
    if (section === ComparisonPeriod.Team || !periodLabel) {
        return _jsx("p", __assign({ className: "text-sm font-semibold text-gray-900" }, { children: title }));
    }
    return (_jsxs("p", __assign({ className: "text-sm font-semibold text-gray-900" }, { children: [title, " (", periodLabel, ")"] })));
}
export var hasTeamAverageTotals = function (totals) {
    return Object.values(totals).some(function (entry) { return entry.teamAverage !== undefined; });
};
